import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalReportsAPI } from '../../services/portalAPI';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import PortalFooter from '../../components/Layout/PortalFooter';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';

interface OrderItem {
  id: number;
  formula_id: number;
  formula_name: string;
  is_pelleted: boolean;
  package_type: string;
  total_quantity_lbs: number;
  number_of_packages: number;
  line_total: number;
}

interface Order {
  id: number;
  order_number: string;
  order_date: string;
  delivery_date: string;
  status: string;
  status_display: string;
  total_weight_lbs: number;
  total_amount: number;
  items: OrderItem[];
  items_count: number;
}

interface OrderHistoryData {
  orders: Order[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
  available_years: number[];
}

const PortalOrderHistory: React.FC = () => {
  const { user } = usePortalAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<OrderHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
  // Server search: input binds to `searchInput` (instant); debounced `searchQuery` (400ms)
  // drives loadOrderHistory. Enter/submit flushes so the user isn't made to wait out the delay.
  const { value: searchInput, setValue: setSearchInput, debounced: searchQuery, flush: flushSearch } =
    useDebouncedSearch('', 400);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);

  const loadOrderHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await portalReportsAPI.getOrderHistory({
        year: selectedYear,
        search: searchQuery || undefined,
        page: currentPage,
        per_page: 15,
      });
      setData(response.data);
    } catch (err: any) {
      setError('Failed to load order history');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, searchQuery, currentPage]);

  useEffect(() => {
    loadOrderHistory();
  }, [loadOrderHistory]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Commit the typed value immediately; the effect on searchQuery does the fetch.
    flushSearch();
    setCurrentPage(1);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = e.target.value ? parseInt(e.target.value) : undefined;
    setSelectedYear(year);
    setCurrentPage(1);
  };

  const handleReorder = (order: Order) => {
    if (!user?.permissions.can_place_orders) {
      alert('You do not have permission to place orders');
      return;
    }

    const reorderData = {
      items: order.items.map(item => ({
        formula: item.formula_id,
        formula_name: item.formula_name,
        is_pellet: item.is_pelleted,
        number_of_packages: item.number_of_packages,
        package_type: item.package_type,
      })),
    };

    localStorage.setItem('reorder_data', JSON.stringify(reorderData));
    navigate('/portal/place-order');
  };

  const toggleOrderExpand = (orderId: number) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const getStatusStyle = (status: string): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      padding: '0.35rem 0.75rem',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '500',
      display: 'inline-block',
    };

    switch (status) {
      case 'PENDING':
        return { ...baseStyle, backgroundColor: '#fef3c7', color: '#92400e' };
      case 'CONFIRMED':
        return { ...baseStyle, backgroundColor: '#dbeafe', color: '#1e40af' };
      case 'IN_PRODUCTION':
        return { ...baseStyle, backgroundColor: '#e0e7ff', color: '#3730a3' };
      case 'DELIVERED':
        return { ...baseStyle, backgroundColor: '#d1fae5', color: '#065f46' };
      default:
        return { ...baseStyle, backgroundColor: 'rgba(195, 173, 139, 0.2)', color: '#3F2021' };
    }
  };

  return (
    <div style={styles.container}>
      <TutorialAutoStart pageKey="order-history" />
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Order History</h1>
          <p style={styles.subtitle}>View your complete order history with search and filtering</p>
        </div>
        <TutorialHelpButton pageKey="order-history" />
      </div>

      {/* Filters */}
      <div style={styles.filtersCard} data-tutorial="history-filters">
        <form onSubmit={handleSearch} style={styles.filtersForm}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Year</label>
            <select
              value={selectedYear || ''}
              onChange={handleYearChange}
              style={styles.select}
            >
              <option value="">All Years</option>
              {data?.available_years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Search</label>
            <div style={styles.searchInputGroup}>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setCurrentPage(1); }}
                placeholder="Order # or product name..."
                style={styles.searchInput}
              />
              <button type="submit" style={styles.searchButton}>
                Search
              </button>
            </div>
          </div>
        </form>

        {data && (
          <div style={styles.resultsCount}>
            Showing {data.orders.length} of {data.total_count} orders
          </div>
        )}
      </div>

      {/* Loading/Error States — full message only on the FIRST load (no data yet).
          During a search refetch the list below stays visible (shows prior rows). */}
      {loading && !data && (
        <div style={styles.loadingState}>Loading order history...</div>
      )}

      {error && (
        <div style={styles.errorState}>{error}</div>
      )}

      {/* Orders List — kept mounted during refetch so the search box never resets. */}
      {!error && data && (
        <>
          {data.orders.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No orders found for the selected filters.</p>
            </div>
          ) : (
            <div style={styles.ordersList} data-tutorial="history-list">
              {data.orders.map((order, index) => (
                <div key={order.id} style={styles.orderCard}>
                  <div
                    style={styles.orderHeader}
                    onClick={() => toggleOrderExpand(order.id)}
                  >
                    <div style={styles.orderHeaderLeft}>
                      <div style={styles.orderNumber}>{order.order_number}</div>
                      <span style={getStatusStyle(order.status)}>
                        {order.status_display}
                      </span>
                    </div>
                    <div style={styles.orderHeaderRight}>
                      <div style={styles.orderMeta}>
                        <span style={styles.orderDate}>
                          {new Date(order.order_date).toLocaleDateString()}
                        </span>
                        <span style={styles.orderWeight}>
                          {order.total_weight_lbs.toLocaleString()} lbs
                        </span>
                        <span style={styles.orderTotal}>
                          ${order.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <span style={styles.expandIcon}>
                        {expandedOrder === order.id ? '−' : '+'}
                      </span>
                    </div>
                  </div>

                  {expandedOrder === order.id && (
                    <div style={styles.orderDetails}>
                      <div style={styles.orderDates}>
                        <span>Order Date: {new Date(order.order_date).toLocaleDateString()}</span>
                        <span>Delivery Date: {new Date(order.delivery_date).toLocaleDateString()}</span>
                      </div>

                      {/* Desktop Table */}
                      <div className="portal-table-desktop" style={{ overflowX: 'auto' }}>
                        <table style={styles.itemsTable}>
                          <thead>
                            <tr>
                              <th style={styles.th}>Product</th>
                              <th style={styles.th}>Type</th>
                              <th style={styles.th}>Quantity</th>
                              <th style={styles.th}>Packages</th>
                              <th style={styles.th}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.items.map(item => (
                              <tr key={item.id}>
                                <td style={styles.td}>{item.formula_name}</td>
                                <td style={styles.td}>{item.is_pelleted ? 'Pellet' : 'Mash'}</td>
                                <td style={styles.td}>{item.total_quantity_lbs.toLocaleString()} lbs</td>
                                <td style={styles.td}>{item.number_of_packages}</td>
                                <td style={styles.td}>
                                  ${item.line_total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Items List */}
                      <div className="portal-cards-mobile">
                        {order.items.map(item => (
                          <div key={item.id} style={{ padding: '0.75rem', borderBottom: '1px solid #C3AD8B', marginBottom: '0.5rem' }}>
                            <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#3F2021' }}>{item.formula_name}</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#508590' }}>
                              <span>{item.is_pelleted ? 'Pellet' : 'Mash'}</span>
                              <span>{item.total_quantity_lbs.toLocaleString()} lbs</span>
                              <span style={{ fontWeight: '600', color: '#E73C3E' }}>${item.line_total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {user?.permissions.can_place_orders && (
                        <div style={styles.orderActions}>
                          <button
                            onClick={() => handleReorder(order)}
                            style={styles.reorderButton}
                            data-tutorial={index === 0 ? "reorder-btn" : undefined}
                          >
                            Reorder These Items
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={currentPage === 1 ? styles.paginationButtonDisabled : styles.paginationButton}
              >
                Previous
              </button>
              <span style={styles.pageInfo}>
                Page {currentPage} of {data.total_pages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(data.total_pages, p + 1))}
                disabled={currentPage === data.total_pages}
                style={currentPage === data.total_pages ? styles.paginationButtonDisabled : styles.paginationButton}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <PortalFooter />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1rem',
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: '#508590',
    margin: 0,
  },
  filtersCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '1rem',
    marginBottom: '1rem',
  },
  filtersForm: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    flexDirection: 'column',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    width: '100%',
  },
  filterLabel: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#3F2021',
  },
  select: {
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    border: '1px solid #C3AD8B',
    fontSize: '16px', // Prevents iOS auto-zoom
    width: '100%',
    backgroundColor: '#FFFFFF',
    minHeight: '44px', // Touch target
  },
  searchInputGroup: {
    display: 'flex',
    gap: '0.5rem',
    width: '100%',
    flexDirection: 'column',
  },
  searchInput: {
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    border: '1px solid #C3AD8B',
    fontSize: '16px', // Prevents iOS auto-zoom
    width: '100%',
    minHeight: '44px', // Touch target
  },
  searchButton: {
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    minHeight: '44px',
    width: '100%',
  },
  resultsCount: {
    marginTop: '1rem',
    fontSize: '0.875rem',
    color: '#508590',
  },
  loadingState: {
    textAlign: 'center',
    padding: '3rem',
    color: '#508590',
    fontSize: '1.125rem',
  },
  errorState: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '4px',
    textAlign: 'center',
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '3rem',
    textAlign: 'center',
    color: '#508590',
  },
  ordersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  orderCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    overflow: 'hidden',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    cursor: 'pointer',
    backgroundColor: '#FAF9F6',
    transition: 'background-color 0.2s',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  orderHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    width: '100%',
  },
  orderNumber: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#3F2021',
  },
  orderHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    width: '100%',
    justifyContent: 'space-between',
  },
  orderMeta: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: 1,
  },
  orderDate: {
    fontSize: '0.8rem',
    color: '#508590',
  },
  orderWeight: {
    fontSize: '0.8rem',
    color: '#3F2021',
    fontWeight: '500',
  },
  orderTotal: {
    fontSize: '0.95rem',
    color: '#E73C3E',
    fontWeight: '600',
  },
  expandIcon: {
    fontSize: '1.5rem',
    color: '#3F2021',
    fontWeight: 'bold',
  },
  orderDetails: {
    padding: '1rem',
    borderTop: '1px solid #C3AD8B',
  },
  orderDates: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
    fontSize: '0.8rem',
    color: '#508590',
    flexDirection: 'column',
  },
  itemsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '1rem',
    fontSize: '0.8rem',
  },
  th: {
    textAlign: 'left',
    padding: '0.5rem',
    borderBottom: '2px solid #C3AD8B',
    fontSize: '0.7rem',
    fontWeight: '600',
    color: '#3F2021',
    textTransform: 'uppercase',
    backgroundColor: '#FAF9F6',
  },
  td: {
    padding: '0.5rem',
    borderBottom: '1px solid #C3AD8B',
    fontSize: '0.75rem',
    color: '#3F2021',
  },
  orderActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '1rem',
    borderTop: '1px solid #C3AD8B',
    marginTop: '1rem',
  },
  reorderButton: {
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.95rem',
    minHeight: '44px',
    width: '100%',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '1.5rem',
    flexWrap: 'wrap',
  },
  paginationButton: {
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    minHeight: '44px',
    flex: '1 1 100px',
  },
  paginationButtonDisabled: {
    backgroundColor: '#C3AD8B',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'not-allowed',
    fontWeight: '500',
    opacity: 0.5,
    minHeight: '44px',
    flex: '1 1 100px',
  },
  pageInfo: {
    fontSize: '0.95rem',
    color: '#3F2021',
  },
};

export default PortalOrderHistory;
